import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Market } from 'frontend-utils/models';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { RouterServiceWithLegacyTransitionTo } from 'lumen/initializers/router-legacy-transition-to';
import type { IssueType } from 'lumen/models/parity-manager/issue';
import type MarketParityParityManagerIssueuploadRoute from 'lumen/routes/market/parity/parity-manager/issueupload';
import type { ModelFrom } from 'lumen/utils/routes';

interface UploadError {
  payload?: {
    errors?: {
      meta?: Record<string, string[]> | null;
    }[];
  };
}

// Right value: internal name to use in the formData when sending attachments
const FILE_FIELD_MAPPING: Record<string, string> = {
  extranet_screenshot: 'parity_manager.issue.upload.extranet_file',
  additional_screenshot: 'parity_manager.issue.upload.additional_file',
};

// Max allowed size of attachments of 10MB (= 10485760 bytes)
const MAX_ISSUE_ATTACHMENT_FILE_SIZE = 10485760;

const QUERY_PARAMS = [
  {
    issueId: { scope: 'controller' },
    subscriptionId: { scope: 'controller' },
    issueType: { scope: 'controller' },
  },
] as const;

export default class MarketParityParityManagerIssueuploadController extends Controller {
  declare model: ModelFrom<MarketParityParityManagerIssueuploadRoute>;

  // @ts-expect-error: Property 'type' is missing in type '{ readonly scope: "controller"; }' but required in type '{ type: ControllerQueryParamType; }'.
  queryParams: Controller['queryParams'] = QUERY_PARAMS;

  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: RouterServiceWithLegacyTransitionTo;

  @tracked issueId?: string;
  @tracked issueType?: IssueType;
  @tracked isUploadingFile = false;
  @tracked subscriptionId?: string;

  get isUploadDisabled(): boolean {
    return Boolean(
      (this.model?.extranetScreenshot && this.model?.additionalScreenshot) ||
      this.isUploadingFile,
    );
  }

  get market(): Market | null {
    return this.hotelAndMarketService.market;
  }

  @action
  checkFileSize(event: Event): void {
    // This is fired in the onchange of the file inputs in the form of the issue upload modal
    // Main purpose is to show feedback to the user if they select files that exceed the limit
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];

    if (file) {
      if (file.size > MAX_ISSUE_ATTACHMENT_FILE_SIZE) {
        void this.alerting.showWarningAlert({
          text: this.intl.t(
            'parity_manager.issue.upload.error.file_size_exceeded',
          ),
        });

        // Clear the value of the file input
        fileInput.value = '';
      }
    }
  }

  @action
  deleteIssueFile(fileField: string): void {
    if (fileField) {
      void this.ajax
        .request(
          `${this.ajax.host}/apigateway/v1/app/paritymanager/delete_attachments?market_id=${this.market?.id}&subscription_id=${this.subscriptionId}&issue_id=${this.issueId}&file_field=${fileField}`,
          {
            method: 'DELETE',
          },
        )
        .then((/* response */) => {
          // This will request the attachments attached to the issue again. We should then see the updated attachments then.
          this.send('refreshRoute');
        })
        .catch((error) => {
          this.errorTracking.captureException(error);
        });
    }
  }

  @action
  downloadIssueFile(fileUrl: string): void {
    openUrl(fileUrl);
  }

  @action
  redirectOnClose(): void {
    this.router.legacyTransitionTo('market.parity.parity-manager');
  }

  @action
  submitIssueUploadForm(): void {
    const formData = new FormData();

    const formFileInputs = document
      .getElementById('issue-upload-form')!
      .querySelectorAll<HTMLInputElement>('input.issue-upload-form-file');

    let areAllSelectedFilesWithinMaxFileSize = true;

    if (formFileInputs.length) {
      formFileInputs.forEach((fileInput) => {
        const file = fileInput.files?.[0];

        if (file) {
          // Do a basic file size check. We don't want people crashing our backend by selecting a file of 1GB
          if (file.size <= MAX_ISSUE_ATTACHMENT_FILE_SIZE) {
            formData.append(fileInput.getAttribute('data-name')!, file);
          } else {
            areAllSelectedFilesWithinMaxFileSize = false;
          }
        }
      });
    }

    if (areAllSelectedFilesWithinMaxFileSize) {
      this.isUploadingFile = true;

      void this.ajax
        .request(
          `${this.ajax.host}/apigateway/v1/app/paritymanager/save_attachments?market_id=${this.market?.id}&subscription_id=${this.subscriptionId}&issue_id=${this.issueId}&issue_type=${this.issueType}`,
          {
            method: 'POST',
            contentType: false,
            processData: false,
            mimeType: 'multipart/form-data',
            data: formData,
          },
        )
        .then((/* response */) => {
          this.isUploadingFile = false;

          // This will request the attachments attached to the issue again. We should then see the updated attachments then.
          this.send('refreshRoute');
        })
        .catch((error: unknown) => {
          if (error) {
            this.errorTracking.captureException(error);
          }

          this.isUploadingFile = false;

          let htmlContent: Element | undefined;
          const uploadError = error as UploadError | undefined;

          // Check if the API returned a specific error message
          if (
            uploadError?.payload?.errors &&
            uploadError.payload.errors.length
          ) {
            const errorMessages = uploadError.payload.errors.reduce<
              (string | undefined)[]
            >((accumulator, { meta }) => {
              const messages = Object.keys(meta ?? {}).map((key) => {
                const fieldTranslationKey = FILE_FIELD_MAPPING[key];
                const errorTranslationKey = meta?.[key]?.[0];

                if (
                  !fieldTranslationKey ||
                  !errorTranslationKey ||
                  !this.intl.exists(fieldTranslationKey) ||
                  !this.intl.exists(errorTranslationKey)
                ) {
                  return undefined;
                }

                return [
                  '<li class="paragraph-300--regular">',
                  `  <span class="paragraph-300--bold">${this.intl.t(fieldTranslationKey)}</span>: ${this.intl.t(errorTranslationKey)}`,
                  '</li>',
                ].join('\n');
              });

              return [...accumulator, ...messages];
            }, []);

            const translationMissing = errorMessages.some(
              (message) => message === undefined,
            );

            if (errorMessages.length && !translationMissing) {
              htmlContent = document.createElement('ul');
              htmlContent.className = 'flex flex-col gap-200';
              htmlContent.innerHTML = errorMessages.join('');
            }
          }

          void this.alerting.showErrorAlert({ htmlContent });
        });
    }
  }
}
